import React, { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Paper,
  Switch,
  Group,
  Text,
  Divider,
  Stack,
  Button,
  Alert,
  Loader,
  Modal,
  Flex,
  ActionIcon,
  Box
} from '@mantine/core';
import { 
  IconFaceId, 
  IconPhoto, 
  IconInfoCircle, 
  IconCheck,
  IconX,
  IconX as IconClose
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { Logo } from '../../AuthenticatedLayout';
import { getUserPreferences, updateUserPreferences, UserPreferences } from '../../api/UserSettings';

export function Settings() {
  const navigate = useNavigate();
  const [opened, setOpened] = useState(true);
  
  const handleClose = () => {
    setOpened(false);
    navigate('/gallery');
  };
  const [preferences, setPreferences] = useState<UserPreferences>({
    enableFaceAnalysis: false,
    enableImageAnalysis: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        setLoading(true);
        const result = await getUserPreferences();
        console.log('User preferences from API:', result.userAttributes);
        console.log('enableImageAnalysis from API:', result.userAttributes.enableImageAnalysis);
        console.log('enableImageAnalysis from sessionStorage:', sessionStorage.getItem('enableImageAnalysis'));
        
        // Try to get values from session storage if API returns undefined
        const enableImageAnalysis = 
          result.userAttributes.enableImageAnalysis !== undefined
            ? result.userAttributes.enableImageAnalysis
            : sessionStorage.getItem('enableImageAnalysis') === 'true';
            
        const enableFaceAnalysis = 
          result.userAttributes.enableFaceAnalysis !== undefined
            ? result.userAttributes.enableFaceAnalysis
            : sessionStorage.getItem('enableFaceAnalysis') === 'true';
        
        setPreferences({
          enableFaceAnalysis,
          enableImageAnalysis
        });
        
        console.log('Set preferences state:', { enableFaceAnalysis, enableImageAnalysis });
      } catch (err) {
        console.error('Failed to load user preferences:', err);
        setError('Failed to load your preferences. Please try refreshing the page.');
      } finally {
        setLoading(false);
      }
    };

    fetchPreferences();
  }, []);

  const handleToggleFaceAnalysis = (checked: boolean) => {
    setPreferences(prev => ({ ...prev, enableFaceAnalysis: checked }));
    setHasChanges(true);
    setSuccess(false);
  };

  const handleToggleImageAnalysis = (checked: boolean) => {
    setPreferences(prev => ({ ...prev, enableImageAnalysis: checked }));
    setHasChanges(true);
    setSuccess(false);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      await updateUserPreferences(preferences);
      setSuccess(true);
      setHasChanges(false);
    } catch (err) {
      console.error('Failed to save preferences:', err);
      setError('Failed to save your preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Modal
        opened={opened}
        onClose={handleClose}
        title={<Flex align="center"><Logo /></Flex>}
        fullScreen
        radius={0}
        transitionProps={{ transition: 'fade', duration: 200 }}
        withCloseButton
        closeButtonProps={{ size: 'lg' }}
      >
        <Container size="md" py="xl">
          <Paper p="xl" withBorder>
            <Stack align="center" gap="md">
              <Loader size="lg" />
              <Text>Loading your preferences...</Text>
            </Stack>
          </Paper>
        </Container>
      </Modal>
    );
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={<Flex align="center" justify="center"><Logo/></Flex>}
      fullScreen
      radius={0}
      transitionProps={{ transition: 'fade', duration: 200 }}
      withCloseButton
      closeButtonProps={{ size: 'lg' }}
    >
      <Container size="md" py="xl">
        <Title order={2} mb="lg">Account Settings</Title>
      
      <Paper p="xl" withBorder mb="lg">
        <Title order={3} mb="md">Privacy & Consent</Title>
        <Text mb="lg" color="dimmed">
          Control how Sort My Shoebox processes your photos. You can change these settings at any time.
        </Text>
        
        <Stack gap="xl">
          <Group justify="space-between" wrap="nowrap">
            <Group>
              <IconFaceId size={24} />
              <div>
                <Text fw={500}>Facial Recognition</Text>
                <Text size="sm" color="dimmed">
                  Detects and identifies people in your photos
                </Text>
              </div>
            </Group>
            <Switch 
              checked={preferences.enableFaceAnalysis} 
              onChange={(event) => handleToggleFaceAnalysis(event.currentTarget.checked)}
              size="md"
              onLabel="ON"
              offLabel="OFF"
            />
          </Group>
          
          <Divider />
          
          <Group justify="space-between" wrap="nowrap">
            <Group>
              <IconPhoto size={24} />
              <div>
                <Text fw={500}>Image Analysis</Text>
                <Text size="sm" color="dimmed">
                  Identifies objects, scenes, and text in your photos
                </Text>
              </div>
            </Group>
            <Switch 
              checked={preferences.enableImageAnalysis} 
              onChange={(event) => handleToggleImageAnalysis(event.currentTarget.checked)}
              size="md"
              onLabel="ON"
              offLabel="OFF"
            />
          </Group>
        </Stack>
        
        {!preferences.enableFaceAnalysis && (
          <Alert icon={<IconInfoCircle size={16} />} color="yellow" mt="lg">
            <Text fw={500}>Limited Functionality</Text>
            <Text size="sm">
              Without facial recognition, features like face tagging, date predictions based on age, 
              and some grouping suggestions will be disabled.
            </Text>
          </Alert>
        )}
        
        {success && (
          <Alert icon={<IconCheck size={16} />} color="green" mt="lg">
            Your preferences have been saved successfully.
          </Alert>
        )}
        
        {error && (
          <Alert icon={<IconX size={16} />} color="red" mt="lg">
            {error}
          </Alert>
        )}
        
        <Group justify="flex-end" mt="xl">
          <Button 
            onClick={handleSave} 
            loading={saving} 
            disabled={!hasChanges}
            color="blue"
          >
            Save Changes
          </Button>
        </Group>
      </Paper>
      
      <Paper p="xl" withBorder>
        <Title order={3} mb="md">Data Management</Title>
        <Text mb="lg" color="dimmed">
          Manage your data and privacy settings.
        </Text>
        
        <Group justify="space-between" wrap="nowrap">
          <div>
            <Text fw={500}>Delete Face Recognition Data</Text>
            <Text size="sm" color="dimmed">
              Permanently remove all facial recognition data associated with your account
            </Text>
          </div>
          <Button variant="outline" color="red">
            Delete Data
          </Button>
        </Group>
      </Paper>
      </Container>
    </Modal>
  );
}
